import { FormPage, FormState, SubmissionPageConfig } from "../types/form";

export const DEFAULT_SUBMISSION_TITLE = "Thank you!";
export const DEFAULT_SUBMISSION_DESCRIPTION = "Form was submitted.";
export const DEFAULT_SUBMISSION_REDIRECT_URL = "https://www.smartsuite.com";

export const isSubmissionPage = (page: FormPage | undefined | null): boolean => {
  return !!page && page.page_type === "submission";
};

export const isInputFormPage = (page: FormPage | undefined | null): boolean => {
  return !!page && page.page_type !== "review" && page.page_type !== "submission";
};

export const getSubmissionPage = (pages: FormPage[] | undefined): FormPage | undefined => {
  return pages?.find(isSubmissionPage);
};

/**
 * Returns the effective submission page config for an end-user form.
 *
 * Resolution order:
 *   1. The dedicated submission page (when one exists on the form_state).
 *   2. Legacy form_state fields (`redirect_to_url` / `display_message`) for forms
 *      that have not yet been migrated to the new Submission Page.
 *   3. Defaults (Message mode with the canonical title + description).
 */
export const getEffectiveSubmissionConfig = (formState: FormState): SubmissionPageConfig => {
  const submissionPage = getSubmissionPage(formState?.pages);
  if (submissionPage?.submission) {
    return normalizeSubmissionConfig(submissionPage.submission);
  }

  if (formState?.redirect_to_url) {
    return {
      mode: "redirect",
      display_icon: true,
      title: DEFAULT_SUBMISSION_TITLE,
      description: DEFAULT_SUBMISSION_DESCRIPTION,
      redirect_url: formState.redirect_to_url,
    };
  }

  return {
    mode: "message",
    display_icon: true,
    title: DEFAULT_SUBMISSION_TITLE,
    description: formState?.display_message
      ? formState.display_message
      : DEFAULT_SUBMISSION_DESCRIPTION,
    redirect_url: null,
  };
};

export const normalizeSubmissionConfig = (
  submission: Partial<SubmissionPageConfig> | null | undefined
): SubmissionPageConfig => {
  const mode: SubmissionPageConfig["mode"] =
    submission?.mode === "redirect" ? "redirect" : "message";

  const trimmedTitle = submission?.title?.trim();
  const trimmedRedirectUrl =
    typeof submission?.redirect_url === "string" ? submission.redirect_url.trim() : "";

  const title = trimmedTitle && trimmedTitle.length > 0 ? trimmedTitle : DEFAULT_SUBMISSION_TITLE;
  const hasRedirectUrl = trimmedRedirectUrl.length > 0;
  let redirectUrl = null;
  if (hasRedirectUrl) {
    redirectUrl = trimmedRedirectUrl;
  } else if (mode === "redirect") {
    redirectUrl = DEFAULT_SUBMISSION_REDIRECT_URL;
  }

  return {
    mode,
    display_icon: submission?.display_icon ?? true,
    title,
    description: submission?.description ?? DEFAULT_SUBMISSION_DESCRIPTION,
    redirect_url: redirectUrl,
  };
};

export const isSubmissionDescriptionEmpty = (
  description: SubmissionPageConfig["description"]
): boolean => {
  if (description == null) return true;
  if (typeof description === "string") return description.trim() === "";
  return !description.html;
};
